import type {
  DocumentHistoryAction,
  DocumentSnapshot,
  DocumentType,
  GeneratedDocument,
  GeneratedDocumentStatus,
} from './types'

export type DocumentLabelLocale = 'fr' | 'en' | 'ar'

type TrilingualLabel = Record<DocumentLabelLocale, string>

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, TrilingualLabel> = {
  certificat_scolarite: {
    fr: 'Certificat de scolarité',
    en: 'School certificate',
    ar: 'شهادة تمدرس',
  },
  attestation_presence: {
    fr: 'Attestation de présence',
    en: 'Attendance certificate',
    ar: 'شهادة حضور',
  },
  releve_notes: {
    fr: 'Relevé de notes',
    en: 'Grade report',
    ar: 'كشف نقاط',
  },
  bulletin: {fr: 'Bulletin', en: 'Report card', ar: 'بطاقة تقييم'},
  attestation_reussite: {
    fr: 'Attestation de réussite',
    en: 'Success certificate',
    ar: 'شهادة نجاح',
  },
  certificat_depart: {
    fr: 'Certificat de départ',
    en: 'Departure certificate',
    ar: 'شهادة مغادرة',
  },
  transfert_scolaire: {
    fr: 'Transfert scolaire',
    en: 'School transfer',
    ar: 'نقل مدرسي',
  },
  payment_attestation: {
    fr: 'Attestation de paiement',
    en: 'Payment certificate',
    ar: 'شهادة دفع',
  },
  parent_convocation: {
    fr: 'Convocation parent',
    en: 'Parent summons',
    ar: 'استدعاء ولي',
  },
  custom: {fr: 'Document personnalisé', en: 'Custom document', ar: 'وثيقة مخصصة'},
}

export const DOCUMENT_STATUS_LABELS: Record<GeneratedDocumentStatus, TrilingualLabel> = {
  draft: {fr: 'Brouillon', en: 'Draft', ar: 'مسودة'},
  generated: {fr: 'Généré', en: 'Generated', ar: 'مولّد'},
  printed: {fr: 'Imprimé', en: 'Printed', ar: 'مطبوع'},
  delivered: {fr: 'Remis', en: 'Delivered', ar: 'مسلّم'},
  archived: {fr: 'Archivé', en: 'Archived', ar: 'مؤرشف'},
  deleted: {fr: 'Supprimé', en: 'Deleted', ar: 'محذوف'},
}

export const HISTORY_ACTION_LABELS: Record<DocumentHistoryAction, TrilingualLabel> = {
  generated: {fr: 'Document généré', en: 'Generated', ar: 'تم الإنشاء'},
  printed: {fr: 'Impression enregistrée', en: 'Printed', ar: 'تم تسجيل الطباعة'},
  downloaded: {fr: 'Téléchargement', en: 'Downloaded', ar: 'تم التحميل'},
  viewed: {fr: 'Consultation', en: 'Viewed', ar: 'تم العرض'},
  delivered: {fr: 'Remis', en: 'Delivered', ar: 'تم التسليم'},
  regenerated: {fr: 'Régénération', en: 'Regenerated', ar: 'إعادة إنشاء'},
  archived: {fr: 'Archivé', en: 'Archived', ar: 'أرشفة'},
  deleted: {fr: 'Supprimé', en: 'Deleted', ar: 'حذف'},
}

export function pickDocLabel(labels: TrilingualLabel, locale: DocumentLabelLocale): string {
  return labels[locale] ?? labels.fr
}

export function getDocumentDisplayFields(doc: GeneratedDocument) {
  const s: DocumentSnapshot | undefined = doc.metadata?.snapshot
  return {
    student: s?.studentName || studentDisplayName(doc.student),
    classroom: s?.classroomName || '—',
    establishment: s?.establishmentName || '—',
    schoolYear: s?.schoolYear || doc.schoolYear || '—',
    documentNumber: s?.documentNumber || doc.documentNumber || '—',
    date: s?.generatedAt || doc.createdAt,
    verifyUrl: s?.verifyUrl || doc.metadata?.verifyUrl,
    templateName: s?.templateName,
  }
}

export function studentDisplayName(
  student: GeneratedDocument['student'] | undefined
): string {
  if (!student || typeof student === 'string') return '—'
  const parts = [student.firstName, student.lastName].filter(Boolean)
  return parts.length ? parts.join(' ') : '—'
}
